import { mxp } from '@analytics/src/mixpanel';
import listingAPI from '@api/jooicez-backend/listing';
import locationAPI from '@api/jooicez-backend/location';
import organizationAPI from '@api/jooicez-backend/organization';
import { useAuth } from '@AuthProvider';
import ErrorCodes from '@constants/error-codes';
import { OutsetaCountryEnum } from '@models/common/outsetaRealm.enum';
import { IFindConnectedAccountsResponse } from '@models/listing/response';
import { IFindLocationResponse, IFindOneLocationResponse } from '@models/location/response';
import { IFindOneOrganizationResponse, IFindOrganizationResponse } from '@models/organization/response';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import queryKeys from '@utils/queryKeys';
import { ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ACCOUNT_SCOPE_DENIED_EVENT } from 'src/utils/accountScope';
import { isClerkAuthEnabled } from 'src/utils/clerkAuth';

import { useDeeplinkContext } from './DeepLinkContext';

export const orgIdStorageKey = 'current-organization-id';

export interface IListingContext {
  // Org
  isOrganizationsLoading: boolean;
  organizations: IFindOrganizationResponse | undefined;
  organizationId: number | undefined;
  setOrganizationId: (newOrgId: number | undefined) => void;
  refetchOrganizations: VoidFunction;
  currentOrganization: IFindOneOrganizationResponse;
  isCurrentOrganizationLoading: boolean;
  refetchCurrentOrganization: VoidFunction;
  // Location
  isLocationsLoading: boolean;
  locations: IFindLocationResponse;
  refetchLocations: VoidFunction;
  locationId: number | undefined;
  setLocationId: (e: number | undefined) => void;
  currentLocation: IFindOneLocationResponse | undefined;
  isCurrentLocationLoading: boolean;
  refetchCurrentLocation: VoidFunction;
  // Connected accounts
  isConnectedAccountsLoading: boolean;
  connectedAccounts: IFindConnectedAccountsResponse;
  refetchConnectedAccounts: VoidFunction;
}

const ApplicationContext = createContext<IListingContext>({
  // Org
  isOrganizationsLoading: false,
  organizations: undefined,
  organizationId: undefined,
  setOrganizationId: () => {},
  refetchOrganizations: () => {},
  currentOrganization: {
    id: -1,
    name: '',
    createdAt: '',
    detail: null,
    aiResponderSettings: null,
  },
  isCurrentOrganizationLoading: false,
  refetchCurrentOrganization: () => {},
  // Location
  isLocationsLoading: false,
  locations: [],
  refetchLocations: () => {},
  locationId: undefined,
  setLocationId: () => {},
  currentLocation: undefined,
  isCurrentLocationLoading: false,
  refetchCurrentLocation: () => {},
  // Connected accounts
  isConnectedAccountsLoading: false,
  connectedAccounts: [],
  refetchConnectedAccounts: () => {},
});

export const ApplicationContextProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { organizationIdParam, locationIdParam, clearLocationIdParam, clearOrganizationIdParam } = useDeeplinkContext();
  const { logout, isLoading: isAuthLoading } = useAuth();
  // Org
  const [organizationId, setOrganizationId] = useState<number>();
  useEffect(() => {
    if (organizationId) localStorage.setItem(orgIdStorageKey, `${organizationId}`);
    // The tracking plan asks for `organization_id` on every event, so it rides
    // along as a Mixpanel super-property rather than per call site.
    if (organizationId) mxp.register({ organization_id: organizationId });
  }, [organizationId]);
  const [organizations, setorganizations] = useState<IFindOrganizationResponse>();
  const [currentOrganization, setCurrentOrganization] = useState<IFindOneOrganizationResponse>({
    name: '',
    id: -1,
    detail: null,
    createdAt: '',
    aiResponderSettings: null,
  });
  const navigate = useNavigate();

  const {
    data: organizationsData,
    isFetching: isOrganizationsLoading,
    refetch: refetchOrganizations,
    isError: isOrganizationsError,
  } = useQuery({
    queryKey: ['getOrganizations'],
    queryFn: organizationAPI.find,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (isOrganizationsError) {
      if (!isClerkAuthEnabled()) logout('token_expired', ErrorCodes.ORGANIZATIONS_REQUEST_FAILED);
      return;
    }
    if (!organizationsData) return;
    const data = organizationsData;

    setorganizations(data.data ?? []);

    if (data.data && data.data.length) {
      const storageOrgId = organizationIdParam || localStorage.getItem(orgIdStorageKey);
      clearOrganizationIdParam();

      if (storageOrgId && data.data.find((x: IFindOrganizationResponse[0]) => x.id === +storageOrgId))
        setOrganizationId(+storageOrgId);
      else setOrganizationId(data.data[0].id);
    } else {
      // Non reindirizzare alla dashboard/onboarding se l'utente è sulla rotta mock (solo UI, nessun backend)
      const isOnOnboardingMock = window.location.pathname.startsWith('/__onboarding-mock');
      // Already on an onboarding step: sending it back to /onboarding races the page's own
      // redirect to that step and leaves a blank page.
      const isOnOnboarding = window.location.pathname.startsWith('/onboarding');
      const country = localStorage.getItem('country_preference');
      if (!isOnOnboardingMock && !isOnOnboarding && country !== OutsetaCountryEnum.SPARTACUS) navigate('/onboarding');
    }
  }, [organizationsData, isOrganizationsError]);

  const {
    data: currentOrgData,
    isFetching: isCurrentOrganizationLoading,
    refetch: refetchCurrentOrganization,
  } = useQuery({
    queryKey: ['findOneOrganization', organizationId],
    queryFn: () => organizationAPI.findOne(organizationId ?? 0),
    refetchOnWindowFocus: false,
    enabled: !!organizationId,
  });

  useEffect(() => {
    if (currentOrgData?.data) {
      setCurrentOrganization(currentOrgData.data);
      refetchLocations();
    }
  }, [currentOrgData]);

  // Locations
  const [locations, setLocations] = useState<IFindLocationResponse>([]);
  const [locationId, setLocationId] = useState<number | undefined>();
  const [currentLocation, setCurrentLocation] = useState<IFindOneLocationResponse>();

  const updateOrganizationId = (newOrgId: number | undefined) => {
    if (organizationId !== newOrgId) setLocationId(undefined);
    setOrganizationId(newOrgId);
  };

  useEffect(() => {
    if (!locations.map((l) => l.id).includes(locationId || -1)) {
      setLocationId(undefined);
    }
  }, [locations]);

  // Invalidate ranking analytics query when switching locations
  const queryClient = useQueryClient();
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: queryKeys.rankingAnalytics.full });
  }, [locationId, queryClient]);

  const {
    data: locationsData,
    isFetching: isLocationsLoading,
    refetch: refetchLocations,
  } = useQuery({
    queryKey: [...queryKeys.locations.find, organizationId],
    enabled: !!organizationId,
    queryFn: async () => {
      if (organizationId) return await locationAPI.find(organizationId);
    },
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (locationsData?.data) {
      const primaryLocation = locationsData.data.find((x: IFindLocationResponse[0]) => x.primary);
      const otherLocations = locationsData.data.filter((x: IFindLocationResponse[0]) => !x.primary);
      setLocations([...(primaryLocation ? [primaryLocation] : []), ...otherLocations]);
      let newLocationId = primaryLocation?.id || otherLocations[0]?.id;
      if (locationId && locationsData.data.find((x: IFindLocationResponse[0]) => x.id === locationId))
        newLocationId = locationId;
      if (locationIdParam && locationsData.data.find((x: IFindLocationResponse[0]) => x.id === locationIdParam))
        newLocationId = locationIdParam;
      clearLocationIdParam();
      setLocationId(newLocationId);
    }
  }, [locationsData]);

  // Account-scope 403 (org/location not in this account): forget the stale selection, then re-pick from the fresh org list.
  useEffect(() => {
    const onDenied = () => {
      localStorage.removeItem(orgIdStorageKey);
      setLocationId(undefined);
      setLocations([]);
      setOrganizationId(undefined);
      refetchOrganizations();
    };
    window.addEventListener(ACCOUNT_SCOPE_DENIED_EVENT, onDenied);
    return () => window.removeEventListener(ACCOUNT_SCOPE_DENIED_EVENT, onDenied);
  }, [refetchOrganizations]);

  const {
    data: currentLocData,
    isFetching: isCurrentLocationLoading,
    refetch: refetchCurrentLocation,
  } = useQuery({
    queryKey: ['findOneLocation', locationId],
    queryFn: () => locationAPI.findOne(locationId ?? 0),
    refetchOnWindowFocus: false,
    enabled: !!locationId,
  });

  useEffect(() => {
    if (currentLocData?.data) setCurrentLocation(currentLocData.data);
  }, [currentLocData]);

  // Connected accounts
  const [connectedAccounts, setConnectedAccounts] = useState<IFindConnectedAccountsResponse>([]);

  const hasAccessToken = isClerkAuthEnabled() || Boolean(localStorage.getItem('Outseta.nocode.accessToken'));

  const {
    data: connectedAccountsData,
    isFetching: isConnectedAccountsLoading,
    refetch: refetchConnectedAccounts,
  } = useQuery({
    queryKey: ['findConnectedAccounts', organizationId],
    queryFn: () => listingAPI.findConnectedAccounts({ organizationId: organizationId ?? 0 }),
    enabled: !!organizationId && hasAccessToken && !isAuthLoading,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (connectedAccountsData?.data) setConnectedAccounts(connectedAccountsData.data);
  }, [connectedAccountsData]);

  return (
    <ApplicationContext.Provider
      value={{
        // Org
        isOrganizationsLoading,
        organizations,
        organizationId,
        setOrganizationId: updateOrganizationId,
        refetchOrganizations,
        currentOrganization,
        isCurrentOrganizationLoading,
        refetchCurrentOrganization,
        // Locations
        isLocationsLoading,
        locations,
        refetchLocations,
        locationId,
        setLocationId,
        currentLocation,
        isCurrentLocationLoading,
        refetchCurrentLocation,
        // Connected accounts
        isConnectedAccountsLoading,
        connectedAccounts,
        refetchConnectedAccounts,
      }}
    >
      {children}
    </ApplicationContext.Provider>
  );
};

export const useApp = () => {
  const context = useContext(ApplicationContext);
  if (context === undefined) {
    throw new Error('useListingContext must be used within a ListingContextProvider');
  }
  return context;
};
